import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from "axios";
import { useGetUserID } from "../hooks/useGetUserID";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from '../config';
import RecipeImage from '../components/RecipeImage';
import HeartIcon from '../components/HeartIcon';
import LoadingSpinner from '../components/LoadingSpinner';
import { useCookies } from 'react-cookie';
import Walkthrough from '../components/Walkthrough';
import { calculateRecipeNutrition, formatNutritionValue } from '../utils/nutritionCalculator';
import SignInPopup from '../components/SignInPopup';
import { useSignInPopup } from '../hooks/useSignInPopup';
import SaveOptionsPopup from '../components/SaveOptionsPopup';
import SearchFrankie from '../components/SearchFrankie';
//import '../styles/searchrecipes.css';
import '../styles/searchrecipes-modern.css';

// Ingredient input component with autocomplete
const IngredientInputWithAutocomplete = ({ index, value, onChange, onRemove, showRemove }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
    const [isTyping, setIsTyping] = useState(false);
    const [previousValue, setPreviousValue] = useState('');
    const [isSelectingManually, setIsSelectingManually] = useState(false);

    const handleValueChange = async (newValue) => {
        // Store the previous value when starting to type
        if (!isTyping && value) {
            setPreviousValue(value);
        }
        
        setIsTyping(true);
        onChange(index, newValue);
        
        // Search for suggestions if value length >= 2
        if (newValue.length >= 2) {
            try {
                const response = await axios.get(`${API_BASE_URL}/cupboard/search/ingredients`, {
                    params: { q: newValue, limit: 10 }
                });
                
                if (response.data.success) {
                    setSuggestions(response.data.ingredients);
                    setShowSuggestions(true);
                    setActiveSuggestionIndex(-1);
                }
            } catch (error) {
                console.error('Error searching ingredients:', error);
                setSuggestions([]);
                setShowSuggestions(false);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setIsSelectingManually(true);
        onChange(index, suggestion.name);
        setShowSuggestions(false);
        setSuggestions([]);
        setIsTyping(false);
        setPreviousValue('');
        
        // Reset the manual selection flag after a short delay
        setTimeout(() => {
            setIsSelectingManually(false);
        }, 100);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = activeSuggestionIndex < suggestions.length - 1 ? activeSuggestionIndex + 1 : 0;
            setActiveSuggestionIndex(nextIndex);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const nextIndex = activeSuggestionIndex > 0 ? activeSuggestionIndex - 1 : suggestions.length - 1;
            setActiveSuggestionIndex(nextIndex);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
                handleSuggestionClick(suggestions[activeSuggestionIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            // Revert to previous value on escape
            if (previousValue) {
                onChange(index, previousValue);
            }
            setIsTyping(false);
            setPreviousValue('');
        }
    };

    const handleBlur = () => {
        // If user is manually selecting, don't do anything
        if (isSelectingManually) {
            return;
        }

        // Delay to allow clicking on suggestions
        setTimeout(() => {
            // Double-check that we're not manually selecting
            if (isSelectingManually) {
                return;
            }

            if (isTyping) {
                if (showSuggestions && suggestions.length > 0) {
                    // Check if current input exactly matches a suggestion
                    const exactMatch = suggestions.find(
                        s => s.name.toLowerCase() === value.toLowerCase()
                    );
                    
                    if (exactMatch) {
                        // Use exact match
                        onChange(index, exactMatch.name);
                    } else {
                        // Auto-select best match only if no manual selection was made
                        const bestMatch = suggestions[0];
                        if (bestMatch) {
                            onChange(index, bestMatch.name);
                        }
                    }
                } else {
                    // No suggestions found, revert to previous value if it exists
                    if (previousValue) {
                        onChange(index, previousValue);
                    }
                }
            }
            
            setShowSuggestions(false);
            setIsTyping(false);
            setPreviousValue('');
        }, 300);
    };

    const handleFocus = () => {
        if (value && value.length >= 2 && suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    return (
        <div className="ingredient-item" style={{ position: 'relative' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <input
                    type="text"
                    className="search-input"
                    placeholder={`Ingredient ${index + 1} - Start typing...`}
                    value={value}
                    onChange={(e) => handleValueChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    autoComplete="off"
                    style={{ 
                        borderColor: isTyping && showSuggestions ? '#007bff' : undefined
                    }}
                />
                {showSuggestions && suggestions.length > 0 && (
                    <div className="suggestions-dropdown" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid #eee',
                            fontSize: '12px',
                            color: '#666'
                        }}>
                            {suggestions.length} ingredient{suggestions.length !== 1 ? 's' : ''} found - Click to select or press Escape to cancel
                        </div>
                        {suggestions.map((suggestion, suggestionIndex) => (
                            <div
                                key={suggestionIndex}
                                className={`suggestion-item ${
                                    activeSuggestionIndex === suggestionIndex ? 'active' : ''
                                }`}
                                onMouseDown={() => {
                                    // Use onMouseDown instead of onClick to fire before onBlur
                                    setIsSelectingManually(true);
                                    handleSuggestionClick(suggestion);
                                }}
                                onMouseEnter={() => setActiveSuggestionIndex(suggestionIndex)}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    backgroundColor: activeSuggestionIndex === suggestionIndex ? '#e9ecef' : 'white',
                                    borderBottom: suggestionIndex < suggestions.length - 1 ? '1px solid #eee' : 'none'
                                }}
                            >
                                {suggestion.name}
                                {suggestionIndex === 0 && (
                                    <span style={{ 
                                        fontSize: '10px', 
                                        color: '#007bff', 
                                        marginLeft: '8px' 
                                    }}>
                                        (Best match)
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {showRemove && (
                <button 
                    className="remove-ingredient-button"
                    onClick={() => onRemove(index)}
                >
                    Remove
                </button>
            )}
        </div>
    );
};

export default function SearchRecipes() {
    const [recipes, setRecipes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchType, setSearchType] = useState("ingredients"); // Default to ingredients for all users
    const [ingredientsList, setIngredientsList] = useState([""]);
    const [servingSizes, setServingSizes] = useState({});
    const [recipeCosts, setRecipeCosts] = useState({});
    const [ingredientPrices, setIngredientPrices] = useState({});
    const [expandedRecipes, setExpandedRecipes] = useState({});
    const [featuredRecipes, setFeaturedRecipes] = useState([]);
    const [savedRecipes, setSavedRecipes] = useState([]);
    const [mealPrepDraftIds, setMealPrepDraftIds] = useState([]);
    const [saveOptionsPopup, setSaveOptionsPopup] = useState({ show: false, recipeId: null, recipeName: null });
    const [cookies] = useCookies(["access_token"]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [searchInterpretation, setSearchInterpretation] = useState(null);
    const [cupboardItems, setCupboardItems] = useState([]);
    const [selectedCupboardItems, setSelectedCupboardItems] = useState([]);
    const [showCupboardSearch, setShowCupboardSearch] = useState(false);
    const [cupboardCollapsed, setCupboardCollapsed] = useState(false);
    
    // Price search state
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    
    // Specials search state
    const [specialsFilter, setSpecialsFilter] = useState("any"); // "any", "with_specials", "without_specials"
    
    // Token validation state
    const [tokenValidated, setTokenValidated] = useState(false);
    const [tokenValidationAttempted, setTokenValidationAttempted] = useState(false);
    
    // Category and dietary filtering state
    const [activeCategory, setActiveCategory] = useState('');
    const [activeDietary, setActiveDietary] = useState('');
    
    // Recipe sorting/filtering state
    const [sortBy, setSortBy] = useState('default'); // 'default', 'price-low', 'price-high', 'smart'
    
    // Advanced filter states
    const [cookingTimeFilter, setCookingTimeFilter] = useState('any'); // 'any', 'quick', 'medium', 'long'
    const [difficultyFilter, setDifficultyFilter] = useState('any'); // 'any', 'easy', 'medium', 'hard'
    const [preferSpecials, setPreferSpecials] = useState(false);
    const [preferCupboard, setPreferCupboard] = useState(false);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    
    // Cost calculation tracking
    const [costsCalculated, setCostsCalculated] = useState(false);
    
    // Nutrition filter state
    const [nutritionFilter, setNutritionFilter] = useState('any'); // 'any', 'low-calorie', 'high-protein', 'low-calorie-high-protein'
    
    // Store nutrition data for all recipes (keyed by recipe ID)
    const [recipesNutrition, setRecipesNutrition] = useState({});
    
    // Reset price sort when costs are no longer calculated
    useEffect(() => {
        if (!costsCalculated && (sortBy === 'price-low' || sortBy === 'price-high')) {
            setSortBy('default');
        }
    }, [costsCalculated, sortBy]);
    
    // Onboarding guide state
    const [showGuide, setShowGuide] = useState(false);
    const [walkthroughStatusChecked, setWalkthroughStatusChecked] = useState(false);
    const [hasSeenWalkthrough, setHasSeenWalkthrough] = useState(false);
    
    const userID = useGetUserID();
    const navigate = useNavigate();
    const location = useLocation();

    // Helper to get auth token from cookies or localStorage
    const getAuthToken = () => cookies.access_token || window.localStorage.getItem('auth_token');

    // Sign-in popup for non-authenticated users (show after 10s, then every 40s)
    const isAuthenticated = !!(userID && getAuthToken());
    const { showPopup: showSignInPopup, handleClosePopup: handleCloseSignInPopup } = useSignInPopup(isAuthenticated, 'searchrecipes');

    // Helper to build Authorization header only when token exists
    const getAuthHeaders = () => {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const recipeCatalogGet = (path, params = {}) => axios.get(`${API_BASE_URL}${path}`, {
        params,
        headers: getAuthHeaders(),
    });

    // Helper to pre-fetch nutrition for a list of recipes
    const preFetchNutrition = async (recipesList) => {
        if (!recipesList || recipesList.length === 0) return;
        
        const missingNutritionRecipes = recipesList.filter(r => !recipesNutrition[r._id]);
        if (missingNutritionRecipes.length === 0) return;

        // Process in parallel
        const nutritionPromises = missingNutritionRecipes.map(async (recipe) => {
            try {
                const nutrition = await calculateRecipeNutrition(
                    recipe._id,
                    recipe.ingredients,
                    recipe.servings || 1
                );
                return { recipeId: recipe._id, nutrition };
            } catch (error) {
                console.error(`Error fetching nutrition for recipe ${recipe._id}:`, error);
                return null;
            }
        });

        const results = await Promise.all(nutritionPromises);
        const newNutrition = {};
        let hasNewData = false;
        
        results.forEach(r => {
            if (r) {
                newNutrition[r.recipeId] = r.nutrition;
                hasNewData = true;
            }
        });

        if (hasNewData) {
            setRecipesNutrition(prev => ({ ...prev, ...newNutrition }));
        }
    };

    // Helper to get available search options based on user status and cupboard
    const getAvailableSearchOptions = () => {
        const isLoggedIn = tokenValidated && userID;
        const hasCupboardItems = cupboardItems.length > 0;
        
        const options = [
            { value: "ingredients", label: "Search by Ingredients" },
            { value: "name", label: "Search by Name" }
        ];
        
        // Add cupboard search only if user is logged in and has cupboard items
        if (isLoggedIn && hasCupboardItems) {
            options.unshift({ value: "cupboard", label: "Search with My Cupboard" });
        }
        
        // Add price and specials search only if user is logged in
        if (isLoggedIn) {
            options.push(
                { value: "price", label: "Search by Price Range" },
                { value: "specials", label: "Search by Specials" }
            );
        }
        
        return options;
    };

    // Mob-style multi-priority sorting algorithm
    const sortRecipes = (recipesToSort) => {
        if (!recipesToSort || recipesToSort.length === 0) return recipesToSort;
        
        const sortedRecipes = [...recipesToSort];
        
        switch (sortBy) {
            case 'smart':
                return sortedRecipes.sort((a, b) => {
                    // FIRST PRIORITY: User preferences (cupboard matches, specials)
                    // Use cupboardMatching data (always available) or fallback to costCalculation
                    const aCupboardMatch = a.cupboardMatching?.ingredientsInCupboard || a.costCalculation?.ingredientsInCupboard || 0;
                    const bCupboardMatch = b.cupboardMatching?.ingredientsInCupboard || b.costCalculation?.ingredientsInCupboard || 0;
                    const aHasSpecials = a.costCalculation?.hasSpecials || false;
                    const bHasSpecials = b.costCalculation?.hasSpecials || false;
                    
                    // Prefer recipes with more cupboard ingredients
                    if (preferCupboard && aCupboardMatch !== bCupboardMatch) {
                        return bCupboardMatch - aCupboardMatch;
                    }
                    
                    // Prefer recipes with specials
                    if (preferSpecials && aHasSpecials !== bHasSpecials) {
                        return bHasSpecials - aHasSpecials;
                    }
                    
                    // SECOND PRIORITY: Ingredient match percentage
                    const aMatchCount = a.matchedIngredientCount || aCupboardMatch || 0;
                    const bMatchCount = b.matchedIngredientCount || bCupboardMatch || 0;
                    const aTotalIngredients = a.ingredients?.length || 1;
                    const bTotalIngredients = b.ingredients?.length || 1;
                    const aMatchPercentage = aMatchCount / aTotalIngredients;
                    const bMatchPercentage = bMatchCount / bTotalIngredients;
                    
                    if (aMatchPercentage !== bMatchPercentage) {
                        return bMatchPercentage - aMatchPercentage;
                    }
                    
                    // THIRD PRIORITY: Cost efficiency (savings per serving)
                    const aCost = a.costCalculation?.totalCost || 0;
                    const bCost = b.costCalculation?.totalCost || 0;
                    const aServings = a.servings || 1;
                    const bServings = b.servings || 1;
                    const aCostPerServing = aCost / aServings;
                    const bCostPerServing = bCost / bServings;
                    
                    if (aCostPerServing !== bCostPerServing) {
                        return aCostPerServing - bCostPerServing;
                    }
                    
                    // FOURTH PRIORITY: Cooking time (shorter is better)
                    const aTime = a.cookingTime || 0;
                    const bTime = b.cookingTime || 0;
                    if (aTime !== bTime) {
                        return aTime - bTime;
                    }
                    
                    // FINAL PRIORITY: Alphabetical by name
                    return (a.name || '').localeCompare(b.name || '');
                });
                
            case 'price-low':
                // Only sort by price if costs are calculated
                if (!costsCalculated) return sortedRecipes;
                return sortedRecipes.sort((a, b) => {
                    const priceA = a.costCalculation?.totalCost || 0;
                    const priceB = b.costCalculation?.totalCost || 0;
                    return priceA - priceB;
                });
                
            case 'price-high':
                // Only sort by price if costs are calculated
                if (!costsCalculated) return sortedRecipes;
                return sortedRecipes.sort((a, b) => {
                    const priceA = a.costCalculation?.totalCost || 0;
                    const priceB = b.costCalculation?.totalCost || 0;
                    return priceB - priceA;
                });
                
            default:
                return sortedRecipes;
        }
    };

    // Helper to filter recipes by additional criteria
    const filterRecipes = (recipesToFilter) => {
        if (!recipesToFilter || recipesToFilter.length === 0) return recipesToFilter;
        
        return recipesToFilter.filter(recipe => {
            // Cooking time filter
            if (cookingTimeFilter !== 'any') {
                const cookingTime = recipe.cookingTime || 0;
                switch (cookingTimeFilter) {
                    case 'quick':
                        if (cookingTime > 30) return false;
                        break;
                    case 'medium':
                        if (cookingTime <= 30 || cookingTime > 90) return false;
                        break;
                    case 'long':
                        if (cookingTime <= 90) return false;
                        break;
                }
            }
            
            // Difficulty filter (based on cooking time and ingredient count)
            if (difficultyFilter !== 'any') {
                const cookingTime = recipe.cookingTime || 0;
                const ingredientCount = recipe.ingredients?.length || 0;
                const difficultyScore = (cookingTime / 60) + (ingredientCount / 10);
                
                switch (difficultyFilter) {
                    case 'easy':
                        if (difficultyScore > 2) return false;
                        break;
                    case 'medium':
                        if (difficultyScore <= 2 || difficultyScore > 4) return false;
                        break;
                    case 'hard':
                        if (difficultyScore <= 4) return false;
                        break;
                }
            }
            
            // Nutrition filter
            if (nutritionFilter !== 'any') {
                const nutrition = recipesNutrition[recipe._id];
                if (!nutrition || !nutrition.perServing) {
                    // If nutrition data is not available, exclude from results when filter is active
                    return false;
                }
                
                const calories = nutrition.perServing.Calories || 0;
                const protein = nutrition.perServing.Protein || 0;
                
                switch (nutritionFilter) {
                    case 'low-calorie':
                        // Low calorie: less than 400 calories per serving
                        if (calories >= 400) return false;
                        break;
                    case 'high-protein':
                        // High protein: at least 20g protein per serving
                        if (protein < 20) return false;
                        break;
                    case 'low-calorie-high-protein':
                        // Low calorie AND high protein: <400 calories AND >=20g protein
                        if (calories >= 400 || protein < 20) return false;
                        break;
                }
            }
            
            return true;
        });
    };

    // Handle category filtering
    const handleCategoryFilter = async (category) => {
        setActiveCategory(category);
        setActiveDietary(''); // Clear dietary filter when selecting category
        setRecipes([]);
        setIsLoading(true);
        setSortBy('default'); // Reset sort when filtering
        setCostsCalculated(false); // Reset costs calculated flag when filtering
        
        // Update URL with category parameter
        const urlParams = new URLSearchParams(window.location.search);
        if (category) {
            urlParams.set('category', category);
            urlParams.delete('dietary'); // Remove dietary param when selecting category
        } else {
            urlParams.delete('category');
        }
        
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.pushState({}, '', newUrl);
        
        try {
            const userId = window.localStorage.getItem('userID');
            let params = {};
            
            if (userId) {
                params.userId = userId;
            }
            
            if (category) {
                params.categories = category;
            }
            
            const response = await recipeCatalogGet('/recipes/search', params);
            
            // Pre-fetch nutrition before showing recipes
            await preFetchNutrition(response.data);
            
            setRecipes(response.data);
        } catch (error) {
            console.error("Error filtering by category:", error);
            alert("Error filtering recipes. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle dietary filtering
    const handleDietaryFilter = async (dietary) => {
        setActiveDietary(dietary);
        setActiveCategory(''); // Clear category filter when selecting dietary
        setRecipes([]);
        setIsLoading(true);
        setSortBy('default'); // Reset sort when filtering
        setCostsCalculated(false); // Reset costs calculated flag when filtering
        
        // Update URL with dietary parameter
        const urlParams = new URLSearchParams(window.location.search);
        if (dietary) {
            urlParams.set('dietary', dietary);
            urlParams.delete('category'); // Remove category param when selecting dietary
        } else {
            urlParams.delete('dietary');
        }
        
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.pushState({}, '', newUrl);
        
        try {
            const userId = window.localStorage.getItem('userID');
            let params = {};
            
            if (userId) {
                params.userId = userId;
            }
            
            if (dietary === 'vegetarian') {
                params.isVegetarian = 'true';
            } else if (dietary === 'vegan') {
                params.isVegan = 'true';
            }
            
            const response = await recipeCatalogGet('/recipes/search', params);
            
            // Pre-fetch nutrition before showing recipes
            await preFetchNutrition(response.data);
            
            setRecipes(response.data);
        } catch (error) {
            console.error("Error filtering by dietary preference:", error);
            alert("Error filtering recipes. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Token validation effect - similar to CreateRecipe.js
    useEffect(() => {
        const validateToken = () => {
            // Check both cookie and localStorage for a token
            const cookieToken = cookies.access_token;
            const localStorageToken = localStorage.getItem('auth_token');
            const hasToken = !!cookieToken || !!localStorageToken;


            if (!hasToken) {
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                // Don't navigate to auth if no token - allow anonymous browsing
                return;
            }

            // Get the token to validate (prefer cookie, fallback to localStorage)
            const token = cookieToken || localStorageToken;
            
            try {
                // Manually validate the token by decoding and checking expiration
                const parts = token.split('.');
                if (parts.length !== 3) {
                    throw new Error('Invalid token format');
                }
                
                // Decode the payload
                const payload = JSON.parse(atob(parts[1]));
                const now = Math.floor(Date.now() / 1000);
                
                // Check if token is expired
                if (payload.exp && payload.exp < now) {
                    setTokenValidated(false);
                    setTokenValidationAttempted(true);
                    // Clear expired token data
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('userID');
                    return;
                }
                
                setTokenValidated(true);
                setTokenValidationAttempted(true);
                
            } catch (tokenError) {
                console.error('Token validation failed in SearchRecipes:', tokenError);
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                // Clear invalid token data
                localStorage.removeItem('auth_token');
                localStorage.removeItem('userID');
            }
        };

        // Run validation immediately
        validateToken();
        
    }, [cookies.access_token]);

    // Listen for auth token updates from login
    useEffect(() => {
        const handleAuthTokenUpdate = (event) => {
            // Force re-validation of the new token
            setTokenValidated(false);
            setTokenValidationAttempted(false);
        };

        window.addEventListener('auth-token-updated', handleAuthTokenUpdate);
        
        return () => {
            window.removeEventListener('auth-token-updated', handleAuthTokenUpdate);
        };
    }, []);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showFilterDropdown && !event.target.closest('.filter-dropdown') && !event.target.closest('.filter-main-btn') && !event.target.closest('.filter-dropdown-backdrop')) {
                setShowFilterDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilterDropdown]);

    // Prevent body scroll when filter is open on mobile
    useEffect(() => {
        if (showFilterDropdown) {
            // Check if mobile (window width <= 768px)
            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
                document.body.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
            }
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [showFilterDropdown]);

    useEffect(() => {
        const fetchFeaturedRecipes = async () => {
            try {
                // Wait for token validation to complete before fetching
                if (!tokenValidationAttempted) {
                    return; // Wait for token validation
                }
                
                // Include userId if logged in so backend can add cupboard matching
                const userId = window.localStorage.getItem('userID');
                const featuredParams = userId ? { userId } : {};

                const response = await recipeCatalogGet('/recipes/featured', featuredParams);
                
                // Pre-fetch nutrition before showing recipes
                await preFetchNutrition(response.data);
                
                setFeaturedRecipes(response.data);
                
            } catch (error) {
                console.error("Error fetching featured recipes:", error);
            } finally {
                setIsInitialLoading(false);
            }
        };

        fetchFeaturedRecipes();
    }, [tokenValidationAttempted]);

    useEffect(() => {
        // Only fetch user-specific data if token validation is complete and token is valid
        if (!tokenValidationAttempted) {
            return; // Wait for token validation
        }

        if (userID && tokenValidated) {
            fetchSavedRecipes();
            fetchMealPrepDraftIds();
            fetchCupboardItems();
            
            // Re-fetch featured recipes with userId to get cupboard matching
            const refetchFeaturedRecipes = async () => {
                try {
                    const response = await recipeCatalogGet('/recipes/featured', { userId: userID });
                    
                    // Pre-fetch nutrition before showing recipes
                    await preFetchNutrition(response.data);
                    
                    setFeaturedRecipes(response.data);
                } catch (error) {
                    console.error("Error re-fetching featured recipes with cupboard matching:", error);
                }
            };
            refetchFeaturedRecipes();
        }
    }, [userID, tokenValidated, tokenValidationAttempted]);

    // Effect to set appropriate default search type based on user status and cupboard
    useEffect(() => {
        if (!tokenValidationAttempted) {
            return; // Wait for token validation
        }

        const isLoggedIn = tokenValidated && userID;
        const hasCupboardItems = cupboardItems.length > 0;
        
        // If user is logged in and has cupboard items, default to cupboard search
        if (isLoggedIn && hasCupboardItems) {
            setSearchType("cupboard");
        } else {
            // For non-logged in users or logged in users without cupboard items, default to ingredients
            setSearchType("ingredients");
        }
    }, [tokenValidated, tokenValidationAttempted, userID, cupboardItems.length]);

    // Fetch walkthrough status from database
    useEffect(() => {
        const fetchWalkthroughStatus = async () => {
            if (!tokenValidationAttempted || !tokenValidated || !userID) {
                setWalkthroughStatusChecked(true);
                return;
            }

            try {
                const response = await axios.get(`${API_BASE_URL}/users/walkthrough/search/status/${userID}`, {
                    headers: getAuthHeaders()
                });
                
                // Store whether user has seen the walkthrough
                setHasSeenWalkthrough(response.data.hasSeenSearchWalkthrough || false);
                setWalkthroughStatusChecked(true);
            } catch (error) {
                console.error("Error fetching walkthrough status:", error);
                // On error, default to not showing the walkthrough (assume they've seen it)
                setHasSeenWalkthrough(true);
                setWalkthroughStatusChecked(true);
            }
        };

        fetchWalkthroughStatus();
    }, [tokenValidated, tokenValidationAttempted, userID]);

    // Initialize guide - Only show for logged-in users on their first visit
    useEffect(() => {
        if (!tokenValidationAttempted || !tokenValidated || !userID || !walkthroughStatusChecked) {
            return; // Wait for status check to complete
        }

        // Don't show guide if user has already seen it
        if (hasSeenWalkthrough) {
            return;
        }

        // Small delay to ensure DOM is ready regardless of recipe loading state
        const timer = setTimeout(() => {
            const searchBar = document.querySelector('.modern-search-bar');
            // Show guide as long as the search bar is present (no longer requires category button)
            if (searchBar) {
                setShowGuide(true);
            } else {
                // Fallback: show anyway if the page is mounted and user is authenticated
                setShowGuide(true);
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [tokenValidated, tokenValidationAttempted, userID, walkthroughStatusChecked, hasSeenWalkthrough]);
    
    const fetchSavedRecipes = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/recipes/savedRecipes/${userID}`, {
                headers: getAuthHeaders()
            });
            
            if (response.data && response.data.savedRecipes) {
                setSavedRecipes(response.data.savedRecipes.map(recipe => recipe._id));
            }
        } catch (error) {
            console.error("Error fetching saved recipes:", error);
        }
    };

    const fetchMealPrepDraftIds = async () => {
        try {
            if (!userID) return;
            const response = await axios.get(`${API_BASE_URL}/recipes/meal-prep-draft/ids/${userID}`, {
                headers: getAuthHeaders()
            });
            if (response.data && response.data.mealPrepDraft) {
                setMealPrepDraftIds(response.data.mealPrepDraft.map(id => id.toString()));
            }
        } catch (error) {
            console.error("Error fetching meal prep draft:", error);
        }
    };

    const fetchCupboardItems = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/cupboard/${userID}`, {
                headers: getAuthHeaders()
            });
            
            if (response.data.success) {
                setCupboardItems(response.data.items);
            }
        } catch (error) {
            console.error("Error fetching cupboard items:", error);
        }
    };

    // Handle URL parameters for category/dietary filtering and search queries
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const categoryParam = urlParams.get('category');
        const dietaryParam = urlParams.get('dietary');
        const queryParam = urlParams.get('query');
        
        if (queryParam) {
            setSearchTerm(queryParam);
            setSearchType('ai'); // Use AI search for query parameter
            // Trigger search after a short delay to ensure state is set
            setTimeout(() => {
                // The search will be triggered by the searchRecipes function being called
                // We'll handle this by checking if searchTerm is set and triggering search
            }, 100);
        } else if (categoryParam) {
            setActiveCategory(categoryParam);
            handleCategoryFilter(categoryParam);
        } else if (dietaryParam) {
            setActiveDietary(dietaryParam);
            handleDietaryFilter(dietaryParam);
        }
    }, [location.search]);

    // When navigating from Cupboard "Search recipes (smart order)": switch to cupboard + smart sort and run search
    useEffect(() => {
        const state = location.state;
        if (!state?.fromCupboardSmart || !userID || !tokenValidated) return;

        const runCupboardSmartSearch = async () => {
            try {
                setSearchType('cupboard');
                setSortBy('smart');
                setActiveCategory('');
                setActiveDietary('');
                setPreferCupboard(true);
                setIsLoading(true);
                const cupboardRes = await axios.get(`${API_BASE_URL}/cupboard/${userID}`, { headers: getAuthHeaders() });
                const items = cupboardRes.data?.success ? cupboardRes.data.items || [] : [];
                if (items.length === 0) {
                    setRecipes([]);
                    setIsLoading(false);
                    navigate('/searchrecipes', { replace: true, state: {} });
                    return;
                }
                const ingredientNames = items.map((item) => item.ingredientName).filter(Boolean);
                const params = {
                    ingredients: ingredientNames.join(','),
                    userId: userID,
                    limit: 30,
                    calculateCosts: 'true'
                };
                const response = await recipeCatalogGet('/recipes/search', params);
                const recipeList = response.data && Array.isArray(response.data) ? response.data : [];
                await preFetchNutrition(recipeList);
                setRecipes(recipeList);
                setCupboardItems(items);
                setSelectedCupboardItems(items.map((item) => item._id));
                setCostsCalculated(true);
            } catch (err) {
                console.error('Cupboard smart search error:', err);
                setRecipes([]);
            } finally {
                setIsLoading(false);
                navigate('/searchrecipes', { replace: true, state: {} });
            }
        };

        runCupboardSmartSearch();
    }, [location.state?.fromCupboardSmart, userID, tokenValidated]);

    

    const searchRecipes = async () => {
        try {
            setIsLoading(true);
            setSearchInterpretation(null);
            setSortBy('default'); // Reset sort when performing new search
            setCostsCalculated(false); // Reset costs calculated flag when performing new search
            
            // If search term is empty, just show featured recipes
            if (!searchTerm.trim()) {
                setRecipes([]);
                setIsLoading(false);
                return;
            }

            // Simple text search - search by name and ingredients
            const params = {
                searchTerm: searchTerm.trim()
            };

            // Add userId for user-specific features (cupboard matching, etc.)
            if (userID) {
                params.userId = userID;
            }

            const response = await recipeCatalogGet('/recipes/search', params);

            if (response.data.length === 0) {
                setRecipes([]);
                setIsLoading(false);
                // Don't show alert, just display empty state
                return;
            }

            // Pre-fetch nutrition before showing recipes
            await preFetchNutrition(response.data);

            setRecipes(response.data);
            setIsLoading(false);
        } catch (error) {
            console.error("Error searching recipes:", error);
            setRecipes([]);
            setIsLoading(false);
        }
    };

    // ─── Frankie voice search handler ────────────────────────────────────────
    // Uses flexible search: splits phrases, expands variants (roasted→roast),
    // requires each term to match. E.g. "roasted chicken" matches "Easy Whole Roast Chicken".
    const handleFrankieSearch = async (params) => {
        try {
            setIsLoading(true);
            setSearchInterpretation(null);
            setSortBy('default');
            setCostsCalculated(false);

            const queryParams = {};
            if (userID) queryParams.userId = userID;

            // Flexible search: combine keywords + ingredients, split phrases into words
            const allTerms = [
                ...(params.keywords || []).flatMap(k => String(k).split(/\s+/)).filter(Boolean),
                ...(params.ingredients || []),
                ...(params.cuisine ? [params.cuisine] : [])
            ];
            const uniqueTerms = [...new Set(allTerms)].filter(t => t.length > 1);
            if (uniqueTerms.length > 0) {
                queryParams.flexibleSearch = 'true';
                queryParams.searchKeywords = uniqueTerms.join(',');
            }

            if (params.isVegan) queryParams.isVegan = 'true';
            else if (params.isVegetarian) queryParams.isVegetarian = 'true';

            if (params.categories?.length) queryParams.categories = params.categories[0];
            if (params.cookingTime) queryParams.cookingTime = params.cookingTime;
            if (params.difficulty) queryParams.difficulty = params.difficulty;

            const response = await recipeCatalogGet('/recipes/search', queryParams);
            const results = response.data || [];
            await preFetchNutrition(results);
            setRecipes(results);
            return { count: results.length };
        } catch (error) {
            console.error('[SearchFrankie] Error executing voice search:', error);
            setRecipes([]);
            return { count: 0 };
        } finally {
            setIsLoading(false);
        }
    };

    const handleIngredientChange = (index, value) => {
        const newIngredients = [...ingredientsList];
        newIngredients[index] = value;
        setIngredientsList(newIngredients);
    };

    const addIngredientField = () => {
        setIngredientsList([...ingredientsList, ""]);
    };

    const removeIngredientField = (index) => {
        const newIngredients = ingredientsList.filter((_, i) => i !== index);
        setIngredientsList(newIngredients);
    };

    const saveRecipe = async (recipeID) => {
        try {
            if (!userID || !getAuthToken()) {
                navigate('/auth');
                return;
            }

            const response = await axios.put(`${API_BASE_URL}/recipes/save`, 
                { recipeID, userID },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    }
                }
            );
            
            setSavedRecipes([...savedRecipes, recipeID]);
            alert(response.data.message || "Recipe added to favorites!");
        } catch (error) {
            console.error("Error favoriting recipe:", error);
            const errorMessage = error.response?.data?.message || "Error favoriting recipe";
            alert(errorMessage);
        }
    };

    const unsaveRecipe = async (recipeID) => {
        try {
            if (!userID || !getAuthToken()) {
                navigate('/auth');
                return;
            }

            await axios.put(`${API_BASE_URL}/recipes/unsave`, 
                { recipeID, userID },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    }
                }
            );
            
            setSavedRecipes(savedRecipes.filter(id => id !== recipeID));
            alert("Recipe removed from favorites!");
        } catch (error) {
            console.error("Error removing from favorites:", error);
            const errorMessage = error.response?.data?.message || "Error removing from favorites";
            alert(errorMessage);
        }
    };

    const addToMealPrepDraft = async (recipeID) => {
        try {
            if (!userID || !getAuthToken()) return;
            await axios.put(`${API_BASE_URL}/recipes/meal-prep-draft/add`, 
                { recipeID, userID },
                { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
            );
            setMealPrepDraftIds(prev => prev.includes(recipeID.toString()) ? prev : [...prev, recipeID.toString()]);
            alert("Added to meal prep draft!");
        } catch (error) {
            console.error("Error adding to meal prep draft:", error);
            alert(error.response?.data?.message || "Error adding to meal prep draft");
        }
    };

    const removeFromMealPrepDraft = async (recipeID) => {
        try {
            if (!userID || !getAuthToken()) return;
            await axios.put(`${API_BASE_URL}/recipes/meal-prep-draft/remove`, 
                { recipeID, userID },
                { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
            );
            setMealPrepDraftIds(prev => prev.filter(id => id !== recipeID.toString()));
            alert("Removed from meal prep draft!");
        } catch (error) {
            console.error("Error removing from meal prep draft:", error);
            alert(error.response?.data?.message || "Error removing from meal prep draft");
        }
    };

    const openSaveOptionsPopup = (recipe) => {
        if (!userID || !getAuthToken()) {
            showSignInPopup();
            return;
        }
        setSaveOptionsPopup({ show: true, recipeId: recipe._id, recipeName: recipe.name });
    };

    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.src = 'path/to/fallback-image.jpg';
    };

    const fetchRecipeCost = async (recipeID) => {
        try {
            const currentServings = servingSizes[recipeID] || recipes.find(r => r._id === recipeID).servings;
            const recipe = recipes.find(r => r._id === recipeID);
            const baseServings = recipe.servings;
            
            const scaledIngredients = recipe.ingredients.map(ing => ({
                ...ing,
                amount: (ing.amount * currentServings) / baseServings
            }));

            const response = await axios.post(`${API_BASE_URL}/recipes/${recipeID}/calculate-cost`, {
                ingredients: scaledIngredients
            });
            
            setRecipeCosts(prevCosts => ({ ...prevCosts, [recipeID]: response.data.totalCost }));
            setIngredientPrices(response.data.ingredientPrices);
        } catch (error) {
            console.error("Error fetching recipe cost:", error);
            alert("Error fetching recipe cost. Please try again.");
        }
    };

    const handleServingSizeConfirm = (recipeId, newServings) => {
        setServingSizes(prev => ({
            ...prev,
            [recipeId]: newServings
        }));
        
        setRecipeCosts(prev => ({
            ...prev,
            [recipeId]: null
        }));
    };

    const toggleRecipeDetails = (recipeId) => {
        setExpandedRecipes(prev => ({
            ...prev,
            [recipeId]: !prev[recipeId]
        }));
    };

    // Compact Mobile Recipe Card Component - Mob-inspired design
    const ModernRecipeCard = ({ recipe }) => {
        const [showSpecialDetails, setShowSpecialDetails] = useState(false);
        
        // Get nutrition data directly from parent state (pre-fetched)
        // No local fetching to prevent glitching
        const nutritionData = recipesNutrition[recipe._id] || null;
        const nutritionReady = !!nutritionData;
        
        // Get dietary category from recipe data
        const getDietaryCategory = () => {
            if (recipe.isVegan) return 'Vegan';
            if (recipe.isVegetarian) return 'Vegetarian';
            if (recipe.categories && recipe.categories.length > 0) {
                // Map category values to display names
                const categoryDisplayNames = {
                    'flavoured chicken': 'Flavoursome Chicken',
                    'break a egg breakfasts': 'Break-a-Egg Breakfasts',
                    'cape malay indian': 'Cape Malay & Indian Spice',
                    'south african': 'Proudly South African',
                    'baked goods': 'Oven-Fresh Baked Goods',
                    'braai & side dishes': 'Braai & Sides',
                    'vegetarian': 'Vegetarian',
                    'decadently healthy desserts': 'Decadently Healthy Desserts',
                    'healthy classics': 'Healthy Classics'
                };
                
                const category = recipe.categories[0].toLowerCase();
                return categoryDisplayNames[category] || 
                    recipe.categories[0].charAt(0).toUpperCase() + recipe.categories[0].slice(1);
            }
            return 'Veggie';
        };

        // Get dietary category CSS class
        const getDietaryClass = () => {
            if (recipe.isVegan) return 'dietary-vegan';
            if (recipe.isVegetarian) return 'dietary-vegetarian';
            return 'dietary-regular';
        };

        // Get cost display - show per serving
        const getCostDisplay = () => {
            if (recipe.costCalculation && recipe.costCalculation.totalCost) {
                const servings = recipe.servings || 1;
                const costPerServing = recipe.costCalculation.totalCost / servings;
                return `R${costPerServing.toFixed(2)} per serving`;
            }
            return null;
        };

        // Handle recipe click
        const handleRecipeClick = () => {
            navigate(`/recipe/${recipe._id}`);
        };
        
        return (
            <div 
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer"
                onClick={handleRecipeClick}
            >
                {/* Compact Recipe Image */}
                <div className="relative">
                    <RecipeImage 
                        imageUrl={recipe.imageUrl} 
                        alt={recipe.name}
                        className="w-full h-32 object-cover"
                    />
                    
                    {/* Free Badge */}
                    {/* <div className="absolute top-1.5 left-1.5">
                        <span className="bg-green-500 text-white px-1.5 py-0.5 rounded-full text-xs font-semibold">
                            Free
                        </span>
                    </div> */}
                    
                    {/* Special Deals Badge */}
                    {recipe.costCalculation?.hasSpecials && (
                        <div className="absolute top-1.5 left-1.5">
                            <span className="bg-orange-500 text-white px-1.5 py-0.5 rounded-full text-xs font-semibold">
                                Special
                            </span>
                        </div>
                    )}
                    
                    {/* Save Icon - opens save options popup */}
                    <div className="absolute top-1.5 right-1.5">
                        <button 
                            className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                openSaveOptionsPopup(recipe);
                            }}
                            aria-label={savedRecipes.includes(recipe._id) ? "Saved - change options" : "Add to saved or meal prep draft"}
                        >
                            <svg 
                                className="w-3 h-3 text-black" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
                                />
                            </svg>
                        </button>
                    </div>
                </div>
                
                {/* Compact Recipe Content */}
                <div className="p-3">
                    <h3 className="font-semibold text-sm text-gray-900 mb-1 line-clamp-2 leading-tight">
                        {recipe.name}
                    </h3>
                    
                    {/* Compact Cook Time */}
                    <div className="flex items-center text-xs text-gray-600 mb-2">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {recipe.cookingTime} mins
                    </div>
                    
                    {/* Compact Servings */}
                    <div className="flex items-center text-xs text-gray-600 mb-2">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {recipe.servings || 1} servings
                    </div>
                    
                    {/* Ingredient Count and Cupboard Match */}
                    <div className="mb-2">
                        {/* Total ingredient count */}
                        <div className="flex items-center text-xs text-gray-600 mb-1">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            {recipe.ingredients?.length || 0} ingredients
                        </div>
                        
                        {/* Cupboard match info - always show if user is logged in (green); blue match count removed as redundant */}
                        {recipe.cupboardMatching && userID && (
                            <div className="flex items-center text-xs text-green-600">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                {recipe.cupboardMatching.ingredientsInCupboard || 0}/{recipe.cupboardMatching.totalIngredients || recipe.ingredients?.length || 0} in cupboard
                            </div>
                        )}
                    </div>
                    
                    {/* Compact Cost Display */}
                    {getCostDisplay() && (
                        <div className="mb-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-600">Cost:</span>
                                <span className="font-semibold text-green-600 text-sm">{getCostDisplay()}</span>
                            </div>
                            {recipe.costCalculation?.cupboardSavings > 0 && (
                                <div className="text-xs text-green-600">
                                    Save R{recipe.costCalculation.cupboardSavings.toFixed(2)} total
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Nutritional Information - Only show when nutrition data is ready to prevent flickering */}
                    {nutritionReady && nutritionData && nutritionData.perServing && (
                        <div className="mb-2 p-2 bg-gray-50 rounded text-xs">
                            <div className="font-semibold text-gray-700 mb-1">Nutrition per serving:</div>
                            <div className="grid grid-cols-2 gap-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Calories:</span>
                                    <span className="font-medium">{formatNutritionValue('Calories', nutritionData.perServing.Calories)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Protein:</span>
                                    <span className="font-medium">{formatNutritionValue('Protein', nutritionData.perServing.Protein)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Carbs:</span>
                                    <span className="font-medium">{formatNutritionValue('Carbs', nutritionData.perServing.Carbs)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Fats:</span>
                                    <span className="font-medium">{formatNutritionValue('Fats', nutritionData.perServing.Fats)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Compact Dietary Tag */}
                    <div className="flex justify-between items-center">
                        <span className={`${getDietaryClass()} px-2 py-0.5 rounded-full text-xs font-medium`}>
                            {getDietaryCategory()}
                        </span>
                        
                        {/* Author */}
                        <span className="text-xs text-gray-500">{recipe.createdBy || 'Mob'}</span>
                    </div>
                </div>
            </div>
        );
    };

    const RecipeCard = ({ recipe }) => {
        const [showSpecialDetails, setShowSpecialDetails] = useState(false);
        
        // Close tooltip when clicking outside
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (showSpecialDetails && !event.target.closest('.special-indicator-small')) {
                    setShowSpecialDetails(false);
                }
            };
            
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }, [showSpecialDetails]);
        
        // Handle special button click/touch
        const handleSpecialButtonClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            setShowSpecialDetails(!showSpecialDetails);
        };
        
        return (
            <div className="recipe-card">
                <div className="recipe-header">
                    <h3 className="recipe-title">{recipe.name}</h3>
                    {/* Small special indicator */}
                    {recipe.costCalculation?.hasSpecials && (
                        <div 
                            className="special-indicator-small"
                            onClick={handleSpecialButtonClick}
                            style={{ cursor: 'pointer' }}
                        >
                            S
                            {/* Special details tooltip */}
                            {showSpecialDetails && (
                                <div className="special-details-tooltip">
                                    <div className="special-tooltip-content">
                                        <div className="special-tooltip-title">Special Deals Available!</div>
                                        {recipe.costCalculation.totalSavings > 0 && (
                                            <div className="special-current-savings">Current savings: R{recipe.costCalculation.totalSavings}</div>
                                        )}
                                        {recipe.costCalculation.hasPotentialSavings && (
                                            <div className="special-potential-savings">
                                                Save R{recipe.costCalculation.potentialSavings} more with alternatives
                                            </div>
                                        )}
                                        {recipe.costCalculation.specialsFound?.length > 0 && (
                                            <div className="special-count">
                                                {recipe.costCalculation.specialsFound.length} special{recipe.costCalculation.specialsFound.length !== 1 ? 's' : ''} applied
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {recipe.createdBy && (
                    <p className="recipe-creator">
                        Created by: {recipe.createdBy}
                    </p>
                )}
                <div className="recipe-image-container">
                    <RecipeImage 
                        imageUrl={recipe.imageUrl} 
                        alt={recipe.name}
                        className="recipe-image"
                    />
                </div>
                
                <div className="recipe-info-row">
                    <p className="cooking-time">⏱️ {recipe.cookingTime} min</p>
                    <p className="servings">👥 {recipe.servings} servings</p>
                </div>
                
                {/* Cupboard Info Display (always shown if logged in); blue match summary removed as redundant */}
                {recipe.cupboardMatching && userID && recipe.cupboardMatching.ingredientsInCupboard > 0 && (
                    <div className="cupboard-info-compact">
                        <span className="cupboard-count-compact">
                            📦 {recipe.cupboardMatching.ingredientsInCupboard}/{recipe.cupboardMatching.totalIngredients} in cupboard
                        </span>
                    </div>
                )}
                
                {/* Compact Cost Display (only shown after calculation) */}
                {recipe.costCalculation && userID && (
                    <div className="cost-summary-compact">
                        <div className="cost-main">
                            <span className="cost-label">Cost per serving:</span>
                            <span className="cost-value">
                                R{((recipe.costCalculation.totalCost || 0) / (recipe.servings || 1)).toFixed(2)}
                            </span>
                        </div>
                        
                        {recipe.costCalculation.cupboardSavings > 0 && (
                            <div className="cost-savings-compact">
                                <span className="savings-compact">
                                     Save R{recipe.costCalculation.cupboardSavings.toFixed(2)} total
                                </span>
                            </div>
                        )}
                    </div>
                )}
                
                <div className="recipe-actions">
                    <HeartIcon
                        filled={savedRecipes.includes(recipe._id)}
                        onClick={() => openSaveOptionsPopup(recipe)}
                        className="favorite-button-large"
                    />
                    <button 
                        onClick={() => navigate(`/recipe/${recipe._id}`)}
                        className="view-details-button"
                    >
                        View Details
                    </button>
                </div>
            </div>
        );
    };

    // Function to render the search interpretation with enhanced metadata
    const renderSearchInterpretation = () => {
        if (!searchInterpretation) return null;
        
        // Format metadata fields with better display names
        const formatMetadataValue = (key, value) => {
            if (!value || (Array.isArray(value) && value.length === 0)) return null;
            
            // Format display based on field type
            switch(key) {
                case 'proteinType':
                    if (value === 'none') return null;
                    return value.charAt(0).toUpperCase() + value.slice(1);
                case 'cookingMethod':
                    if (value === 'other') return null;
                    return value.charAt(0).toUpperCase() + value.slice(1);
                case 'cuisine':
                    if (value === 'other') return null;
                    // Format hyphenated values
                    return value.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ');
                default:
                    if (Array.isArray(value)) {
                        return value.map(item => 
                            item.charAt(0).toUpperCase() + item.slice(1)
                        ).join(", ");
                    }
                    return value;
            }
        };
        
        const hasQuery = searchTerm && searchTerm.trim().length > 0;
        return (
            <div className="search-interpretation">
                <h3>Here are recipes that match what you&apos;re looking for</h3>
                {hasQuery && (
                    <p className="search-interpretation-vibe">You said you were in the mood for something like: &ldquo;{searchTerm.trim()}&rdquo;</p>
                )}
                <p className="search-interpretation-sub">Here&apos;s what I matched to that:</p>
                
                {/* Basic search criteria */}
                {searchInterpretation.keywords?.length > 0 && (
                    <p><strong>Keywords:</strong> {searchInterpretation.keywords.join(", ")}</p>
                )}
                {searchInterpretation.ingredients?.length > 0 && (
                    <p><strong>Ingredients:</strong> {searchInterpretation.ingredients.join(", ")}</p>
                )}
                {searchInterpretation.excludeIngredients?.length > 0 && (
                    <p><strong>Excluding:</strong> {searchInterpretation.excludeIngredients.join(", ")}</p>
                )}
                
                {/* Dietary preferences */}
                {searchInterpretation.isVegan && <p><strong>Dietary:</strong> Vegan</p>}
                {!searchInterpretation.isVegan && searchInterpretation.isVegetarian && (
                    <p><strong>Dietary:</strong> Vegetarian</p>
                )}
                
                {/* Basic categories */}
                {searchInterpretation.categories?.length > 0 && (
                    <p><strong>Categories:</strong> {searchInterpretation.categories.join(", ")}</p>
                )}
                
                {/* Advanced metadata fields */}
                {formatMetadataValue('proteinType', searchInterpretation.proteinType) && (
                    <p><strong>Protein:</strong> {formatMetadataValue('proteinType', searchInterpretation.proteinType)}</p>
                )}
                
                {formatMetadataValue('cookingMethod', searchInterpretation.cookingMethod) && (
                    <p><strong>Cooking:</strong> {formatMetadataValue('cookingMethod', searchInterpretation.cookingMethod)}</p>
                )}
                
                {formatMetadataValue('cuisine', searchInterpretation.cuisine) && (
                    <p><strong>Cuisine:</strong> {formatMetadataValue('cuisine', searchInterpretation.cuisine)}</p>
                )}
                
                {searchInterpretation.flavorProfile?.length > 0 && (
                    <p><strong>Flavors:</strong> {formatMetadataValue('flavorProfile', searchInterpretation.flavorProfile)}</p>
                )}
                
                {searchInterpretation.occasions?.length > 0 && (
                    <p><strong>Occasion:</strong> {formatMetadataValue('occasions', searchInterpretation.occasions)}</p>
                )}
                
                {searchInterpretation.difficulty && (
                    <p><strong>Difficulty:</strong> {formatMetadataValue('difficulty', searchInterpretation.difficulty)}</p>
                )}
            </div>
        );
    };

    // Show full-page loading spinner during initial load
    if (isInitialLoading) {
        return (
            <div className="full-page-loading">
                <LoadingSpinner size="xl" color="primary" text="Loading featured recipes..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Search Bar Section */}
            <div className="bg-white py-4 border-b border-gray-100">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative flex items-center" data-guide-target="search-bar">
                        {/* Search Icon */}
                        <div className="absolute left-4 pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        
                        {/* Search Input */}
                        <input
                            type="text"
                            className="w-full pl-12 pr-32 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent placeholder-gray-400 transition-all"
                            placeholder="Search for recipes, ingredients, etc..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    searchRecipes();
                                }
                            }}
                            autoComplete="off"
                            autoCapitalize="off"
                            autoCorrect="off"
                            spellCheck="false"
                        />
                        
                        {/* Search Button */}
                        <button
                            type="button"
                            onClick={() => searchRecipes()}
                            className="absolute right-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Category Navigation Tabs */}
            <div className="category-nav-container">
                <div className="category-nav-tabs">
                        <button 
                            onClick={() => {
                                setActiveCategory('');
                                setActiveDietary('');
                                setRecipes([]);
                                
                                // Clear URL parameters when clicking "All"
                                const urlParams = new URLSearchParams(window.location.search);
                                urlParams.delete('category');
                                urlParams.delete('dietary');
                                const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
                                window.history.pushState({}, '', newUrl);
                                
                                navigate('/searchrecipes');
                            }}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                !activeCategory && !activeDietary 
                                    ? 'category-button-active bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/25 border border-green-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800 border border-gray-200 hover:border-gray-300 hover:shadow-md'
                            }`}
                        >
                            All
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('flavoured chicken')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'flavoured chicken' 
                                    ? 'category-button-active bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25 border border-orange-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-orange-50 hover:text-orange-700 border border-gray-200 hover:border-orange-200 hover:shadow-md'
                            }`}
                        >
                            Flavoursome Chicken
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('break a egg breakfasts')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'break a egg breakfasts' 
                                    ? 'category-button-active bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-500/25 border border-yellow-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-yellow-50 hover:text-yellow-700 border border-gray-200 hover:border-yellow-200 hover:shadow-md'
                            }`}
                        >
                            Break-a-Egg Breakfasts
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('cape malay indian')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'cape malay indian' 
                                    ? 'category-button-active bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/25 border border-purple-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-purple-50 hover:text-purple-700 border border-gray-200 hover:border-purple-200 hover:shadow-md'
                            }`}
                        >
                            Cape Malay & Indian Spice
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('south african')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'south african' 
                                    ? 'category-button-active bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/25 border border-green-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-green-50 hover:text-green-700 border border-gray-200 hover:border-green-200 hover:shadow-md'
                            }`}
                        >
                            Proudly South African
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('baked goods')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'baked goods' 
                                    ? 'category-button-active bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25 border border-amber-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-amber-50 hover:text-amber-700 border border-gray-200 hover:border-amber-200 hover:shadow-md'
                            }`}
                        >
                            Oven-Fresh Baked Goods
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('braai & side dishes')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'braai & side dishes' 
                                    ? 'category-button-active bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25 border border-red-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-700 border border-gray-200 hover:border-red-200 hover:shadow-md'
                            }`}
                        >
                            Braai & Sides
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('vegetarian')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'vegetarian' 
                                    ? 'category-button-active bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25 border border-emerald-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200 hover:border-emerald-200 hover:shadow-md'
                            }`}
                        >
                            Garden Fresh Veggie
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('decadently healthy desserts')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'decadently healthy desserts' 
                                    ? 'category-button-active bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-lg shadow-pink-500/25 border border-pink-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-pink-50 hover:text-pink-700 border border-gray-200 hover:border-pink-200 hover:shadow-md'
                            }`}
                        >
                            Decadently Healthy Desserts
                        </button>
                        <button 
                            onClick={() => handleCategoryFilter('healthy classics')}
                            className={`category-button flex-shrink-0 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 lg:px-3 lg:py-2 lg:text-sm xl:px-2.5 xl:py-1.5 xl:text-xs 2xl:px-2 2xl:py-1 2xl:text-xs ${
                                activeCategory === 'healthy classics' 
                                    ? 'category-button-active bg-gradient-to-r from-cyan-500 to-cyan-600 text-white shadow-lg shadow-cyan-500/25 border border-cyan-400' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-cyan-50 hover:text-cyan-700 border border-gray-200 hover:border-cyan-200 hover:shadow-md'
                            }`}
                        >
                            Healthy Classics
                        </button>
                </div>
            </div>

            {/* Clean Filter Button - Mob Style */}
            {(recipes.length > 0 || featuredRecipes.length > 0) && (
                <div className="bg-white border-b border-gray-100 py-3">
                    <div className="px-3 max-w-7xl mx-auto">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <h3 className="text-sm font-medium text-gray-700">Filter & Sort</h3>
                                {(sortBy !== 'default' || cookingTimeFilter !== 'any' || difficultyFilter !== 'any' || preferSpecials || preferCupboard || nutritionFilter !== 'any') && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Active
                                    </span>
                                )}
                            </div>
                            
                            <div className="relative">
                                <button
                                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                    className="filter-main-btn"
                                    data-guide-target="filter-button"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                                    </svg>
                                    Filter
                                    <svg className={`w-4 h-4 ml-2 transition-transform duration-200 ${showFilterDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                
                                {/* Filter Dropdown */}
                                {showFilterDropdown && (
                                    <>
                                        {/* Backdrop for mobile */}
                                        <div 
                                            className="filter-dropdown-backdrop"
                                            onClick={() => setShowFilterDropdown(false)}
                                        ></div>
                                        <div className="filter-dropdown">
                                            <div className="filter-dropdown-content">
                                                {/* Mobile Drag Handle */}
                                                <div className="filter-drag-handle"></div>
                                                
                                                {/* Mobile Header with Close Button */}
                                                <div className="filter-mobile-header">
                                                    <h3 className="filter-mobile-title">Filter & Sort</h3>
                                                    <button
                                                        onClick={() => setShowFilterDropdown(false)}
                                                        className="filter-close-button"
                                                        aria-label="Close filter"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            {/* Sort Options */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">Sort By</h4>
                                                <div className="filter-options">
                                                    {[
                                                        { value: 'default', label: 'Default Order' },
                                                        { value: 'smart', label: 'Smart Sort' },
                                                        ...(costsCalculated ? [
                                                            { value: 'price-low', label: 'Price: Low to High' },
                                                            { value: 'price-high', label: 'Price: High to Low' }
                                                        ] : [])
                                                    ].map(option => (
                                                        <label key={option.value} className="filter-option">
                                                            <input
                                                                type="radio"
                                                                name="sortBy"
                                                                value={option.value}
                                                                checked={sortBy === option.value}
                                                                onChange={(e) => setSortBy(e.target.value)}
                                                                className="filter-radio"
                                                            />
                                                            <span className="filter-option-label">{option.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Nutrition Filter */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">Nutrition</h4>
                                                <div className="filter-options">
                                                    {[
                                                        { value: 'any', label: 'Any Nutrition' },
                                                        { value: 'low-calorie', label: 'Low Calorie (<400 cal/serving)' },
                                                        { value: 'high-protein', label: 'High Protein (≥20g/serving)' },
                                                        { value: 'low-calorie-high-protein', label: 'Low Calorie & High Protein' }
                                                    ].map(option => (
                                                        <label key={option.value} className="filter-option">
                                                            <input
                                                                type="radio"
                                                                name="nutrition"
                                                                value={option.value}
                                                                checked={nutritionFilter === option.value}
                                                                onChange={async (e) => {
                                                                    const newFilter = e.target.value;
                                                                    setNutritionFilter(newFilter);
                                                                    
                                                                    // If nutrition filter is selected and we don't have nutrition data for recipes, fetch it
                                                                    if (newFilter !== 'any') {
                                                                        const currentRecipes = activeCategory || activeDietary ? recipes : featuredRecipes;
                                                                        const recipesNeedingNutrition = currentRecipes.filter(r => !recipesNutrition[r._id]);
                                                                        
                                                                        if (recipesNeedingNutrition.length > 0) {
                                                                            // Fetch nutrition for recipes that don't have it
                                                                            const nutritionPromises = recipesNeedingNutrition.map(async (recipe) => {
                                                                                try {
                                                                                    const nutrition = await calculateRecipeNutrition(
                                                                                        recipe._id,
                                                                                        recipe.ingredients,
                                                                                        recipe.servings || 1
                                                                                    );
                                                                                    return { recipeId: recipe._id, nutrition };
                                                                                } catch (error) {
                                                                                    console.error(`Error fetching nutrition for recipe ${recipe._id}:`, error);
                                                                                    return null;
                                                                                }
                                                                            });
                                                                            
                                                                            const nutritionResults = await Promise.all(nutritionPromises);
                                                                            const newNutritionData = {};
                                                                            nutritionResults.forEach(result => {
                                                                                if (result) {
                                                                                    newNutritionData[result.recipeId] = result.nutrition;
                                                                                }
                                                                            });
                                                                            
                                                                            setRecipesNutrition(prev => ({
                                                                                ...prev,
                                                                                ...newNutritionData
                                                                            }));
                                                                        }
                                                                    }
                                                                }}
                                                                className="filter-radio"
                                                            />
                                                            <span className="filter-option-label">{option.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Quick Filters */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">Quick Filters</h4>
                                                <div className="filter-options">
                                                    <label className="filter-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={preferCupboard}
                                                            onChange={(e) => setPreferCupboard(e.target.checked)}
                                                            className="filter-checkbox"
                                                        />
                                                        <span className="filter-option-label">Prefer Cupboard Ingredients</span>
                                                    </label>
                                                    <label className="filter-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={preferSpecials}
                                                            onChange={(e) => setPreferSpecials(e.target.checked)}
                                                            className="filter-checkbox"
                                                        />
                                                        <span className="filter-option-label">Prefer Special Deals</span>
                                                    </label>
                                                </div>
                                            </div>
                                            
                                            {/* Time & Difficulty */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">Cooking Time</h4>
                                                <div className="filter-options">
                                                    {[
                                                        { value: 'any', label: 'Any Time' },
                                                        { value: 'quick', label: 'Quick (≤30 min)' },
                                                        { value: 'medium', label: 'Medium (30-90 min)' },
                                                        { value: 'long', label: 'Long (>90 min)' }
                                                    ].map(option => (
                                                        <label key={option.value} className="filter-option">
                                                            <input
                                                                type="radio"
                                                                name="cookingTime"
                                                                value={option.value}
                                                                checked={cookingTimeFilter === option.value}
                                                                onChange={(e) => setCookingTimeFilter(e.target.value)}
                                                                className="filter-radio"
                                                            />
                                                            <span className="filter-option-label">{option.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">Difficulty</h4>
                                                <div className="filter-options">
                                                    {[
                                                        { value: 'any', label: 'Any Level' },
                                                        { value: 'easy', label: 'Easy' },
                                                        { value: 'medium', label: 'Medium' },
                                                        { value: 'hard', label: 'Hard' }
                                                    ].map(option => (
                                                        <label key={option.value} className="filter-option">
                                                            <input
                                                                type="radio"
                                                                name="difficulty"
                                                                value={option.value}
                                                                checked={difficultyFilter === option.value}
                                                                onChange={(e) => setDifficultyFilter(e.target.value)}
                                                                className="filter-radio"
                                                            />
                                                            <span className="filter-option-label">{option.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Actions */}
                                            <div className="filter-actions">
                                                <button
                                                    onClick={() => {
                                                        setSortBy('default');
                                                        setCookingTimeFilter('any');
                                                        setDifficultyFilter('any');
                                                        setPreferSpecials(false);
                                                        setPreferCupboard(false);
                                                        setNutritionFilter('any');
                                                    }}
                                                    className="filter-reset-all-btn"
                                                >
                                                    Reset All
                                                </button>
                                                <button
                                                    onClick={() => setShowFilterDropdown(false)}
                                                    className="filter-apply-btn"
                                                >
                                                    Apply Filters
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Commented out search container for now */}
            {/* 
            <div className="search-section">
                <div className="search-container">
                    <h1 className="search-title">Discover Amazing Recipes</h1>
                    <p className="search-description">
                        {searchType === "ai" 
                            ? "Tell us what you're in the mood for and we'll find recipes for you" 
                            : searchType === "name" 
                                ? "Find the perfect recipe by name" 
                                : searchType === "cupboard"
                                    ? "Find recipes using ingredients from your cupboard"
                                    : searchType === "price"
                                        ? "Find recipes within your budget range"
                                        : searchType === "specials"
                                            ? "Find recipes with special deals and discounts"
                                            : "Search recipes using ingredients you have"}
                    </p>
                    
                    <div className="search-form">
                        <select 
                            className="search-type-select"
                            value={searchType} 
                            onChange={(e) => {
                                setSearchType(e.target.value);
                                setRecipes([]);
                                setSearchInterpretation(null);
                                setShowCupboardSearch(false);
                                setMinPrice("");
                                setMaxPrice("");
                                setSpecialsFilter("any");
                            }}
                        >
                            {getAvailableSearchOptions().map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        {searchType === "ai" ? (
                            <div className="ai-search-container">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Describe what you're in the mood for..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        ) : searchType === "name" ? (
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Enter recipe name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        ) : searchType === "cupboard" ? (
                            <div className="cupboard-search-container">
                                <button
                                    type="button"
                                    className="cupboard-toggle-button"
                                    onClick={() => setCupboardCollapsed(prev => !prev)}
                                    aria-label={cupboardCollapsed ? "Expand cupboard list" : "Collapse cupboard list"}
                                >
                                    {cupboardCollapsed ? "▼" : "▲"}
                                </button>

                                <p className="cupboard-search-description">
                                    Select ingredients from your cupboard to find matching recipes
                                </p>

                                {!cupboardCollapsed && (
                                <div className="cupboard-ingredients-grid">
                                    {cupboardItems.map(item => (
                                        <label 
                                            key={item._id} 
                                            className={`cupboard-ingredient-checkbox ${selectedCupboardItems.includes(item._id) ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedCupboardItems.includes(item._id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedCupboardItems([...selectedCupboardItems, item._id]);
                                                    } else {
                                                        setSelectedCupboardItems(selectedCupboardItems.filter(id => id !== item._id));
                                                    }
                                                }}
                                            />
                                            <div className="cupboard-ingredient-info">
                                                <span className="ingredient-name">{item.ingredientName}</span>
                                                <span className="ingredient-amount">{item.amount} {item.unit}</span>
                                                <span className="ingredient-location">📍 {item.location}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                )}

                                <div className="cupboard-actions">
                                    <button 
                                        type="button"
                                        className="select-all-cupboard-btn"
                                        onClick={() => setSelectedCupboardItems(cupboardItems.map(item => item._id))}
                                    >
                                        Select All
                                    </button>
                                    <button 
                                        type="button"
                                        className="clear-all-cupboard-btn"
                                        onClick={() => setSelectedCupboardItems([])}
                                    >
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        ) : searchType === "price" ? (
                            <div className="price-search-container">
                                <div className="price-inputs">
                                    <div className="price-input-group">
                                        <label htmlFor="minPrice">Minimum Price (R)</label>
                                        <input
                                            id="minPrice"
                                            type="number"
                                            className="price-input"
                                            placeholder="0.00"
                                            value={minPrice}
                                            onChange={(e) => setMinPrice(e.target.value)}
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="price-input-group">
                                        <label htmlFor="maxPrice">Maximum Price (R)</label>
                                        <input
                                            id="maxPrice"
                                            type="number"
                                            className="price-input"
                                            placeholder="100.00"
                                            value={maxPrice}
                                            onChange={(e) => setMaxPrice(e.target.value)}
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                                <p className="price-search-note">
                                    Prices are calculated based on current ingredient costs and your cupboard items
                                </p>
                                <p className="price-sorting-note">
                                    Results are sorted by price (lowest to highest)
                                </p>
                            </div>
                        ) : searchType === "specials" ? (
                            <div className="specials-search-container">
                                <div className="specials-filter-group">
                                    <label htmlFor="specialsFilter">Show recipes:</label>
                                    <select
                                        id="specialsFilter"
                                        className="specials-filter-select"
                                        value={specialsFilter}
                                        onChange={(e) => setSpecialsFilter(e.target.value)}
                                    >
                                        <option value="any">With or without specials</option>
                                        <option value="with_specials">Only recipes with special deals</option>
                                        <option value="without_specials">Only recipes without specials</option>
                                    </select>
                                </div>
                                <p className="specials-search-note">
                                    Special deals include discounts, bulk offers, and promotional prices
                                </p>
                                <p className="specials-sorting-note">
                                    Results are sorted by savings amount (biggest specials first)
                                </p>
                            </div>
                        ) : (
                            <div className="ingredients-list-container">
                                {ingredientsList.map((ingredient, index) => (
                                    <IngredientInputWithAutocomplete
                                        key={index}
                                        index={index}
                                        value={ingredient}
                                        onChange={handleIngredientChange}
                                        onRemove={removeIngredientField}
                                        showRemove={index > 0}
                                    />
                                ))}
                                <button 
                                    className="add-ingredient-button"
                                    onClick={addIngredientField}
                                >
                                    Add Ingredient
                                </button>
                            </div>
                        )}
                        
                        <button 
                            className="search-button"
                            onClick={searchRecipes}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <LoadingSpinner size="sm" color="white" text="" />
                            ) : (
                                <span className="search-button-text">Search</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            */}

            {/* Compact Mobile Recipes Section */}
            <div className="py-4 bg-white">
                <div className="px-3 max-w-7xl mx-auto">
                {isLoading ? (
                        <div className="flex justify-center items-center py-8">
                        <LoadingSpinner size="lg" color="primary" text="Searching for recipes..." />
                    </div>
                ) : recipes.length > 0 ? (
                    <div>
                            <div className="mb-4">
                                <h2 className="font-semibold text-lg text-gray-900 mb-2">I found some recipes for you</h2>
                        {renderSearchInterpretation()}
                                
                                {/* Compact Cost Calculation Button */}
                                {userID && (
                                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200" data-guide-target="calculate-costs-container">
                                        <div className="flex flex-col gap-2">
                                            <div>
                                                <h3 className="font-semibold text-green-800 text-sm">Calculate Recipe Costs</h3>
                                                <p className="text-xs text-green-700">Get accurate pricing for all recipes</p>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    setIsLoading(true);
                                                    try {
                                                        // Re-fetch with same context so filter and nutrition stay (same recipe set + costs)
                                                        const params = {};
                                                        if (userID) {
                                                            params.userId = userID;
                                                            params.calculateCosts = 'true';
                                                        }
                                                        // Preserve current source: cupboard, search term, or category/dietary
                                                        if (cupboardItems.length > 0) {
                                                            const ingredientNames = cupboardItems.map((item) => item.ingredientName).filter(Boolean);
                                                            if (ingredientNames.length > 0) {
                                                                params.ingredients = ingredientNames.join(',');
                                                                params.limit = 30;
                                                            }
                                                        }
                                                        if (!params.ingredients && searchTerm && searchTerm.trim()) {
                                                            params.searchTerm = searchTerm.trim();
                                                        }
                                                        if (!params.ingredients && !params.searchTerm) {
                                                            if (activeCategory) {
                                                                params.categories = activeCategory;
                                                            } else if (activeDietary) {
                                                                if (activeDietary === 'vegetarian') params.isVegetarian = 'true';
                                                                else if (activeDietary === 'vegan') params.isVegan = 'true';
                                                            }
                                                        }
                                                        const response = await recipeCatalogGet('/recipes/search', params);
                                                        const recipeList = response.data && Array.isArray(response.data) ? response.data : [];
                                                        await preFetchNutrition(recipeList);
                                                        setRecipes(recipeList);
                                                        setCostsCalculated(true);
                                                    } catch (error) {
                                                        console.error('Error calculating costs:', error);
                                                        alert('Error calculating recipe costs. Please try again.');
                                                    } finally {
                                                        setIsLoading(false);
                                                    }
                                                }}
                                                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors w-full"
                                                data-guide-target="calculate-costs-button"
                                            >
                                                Calculate All Costs
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-2 xl:grid-cols-4 xl:gap-1.5 2xl:grid-cols-5 2xl:gap-1">
                            {sortRecipes(filterRecipes(recipes)).map((recipe, index) => (
                                    <div key={recipe._id} data-recipe-id={recipe._id} data-guide-target={index === 0 ? "first-recipe" : undefined}>
                                        <ModernRecipeCard recipe={recipe} />
                                    </div>
                            ))}
                        </div>
                    </div>
                ) : (
                        <div>
                            <div className="text-center mb-6">
                                <h2 className="font-semibold text-xl text-gray-900 mb-2">
                                    {activeCategory ? `${activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} Recipes` :
                                     activeDietary ? `${activeDietary.charAt(0).toUpperCase() + activeDietary.slice(1)} Recipes` :
                                     'Featured Recipes'}
                                </h2>
                                <p className="text-sm text-gray-600">
                                    {activeCategory ? `Delicious ${activeCategory} recipes for you to try` :
                                     activeDietary ? `Tasty ${activeDietary} recipes for you to enjoy` :
                                     'Handpicked recipes for you to try'}
                                </p>
                                
                                {/* Compact Cost Calculation Button for Featured Recipes */}
                                {userID && (
                                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200" data-guide-target="calculate-costs-container">
                                        <div className="flex flex-col gap-2">
                                            <div>
                                                <h3 className="font-semibold text-green-800 text-sm">Calculate Recipe Costs</h3>
                                                <p className="text-xs text-green-700">
                                                    Get accurate pricing for all {activeCategory || activeDietary ? 'filtered' : 'featured'} recipes
                                                </p>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    setIsLoading(true);
                                                    try {
                                                        // Re-fetch with cost calculations
                                                        if (activeCategory || activeDietary) {
                                                            // Refetch category/dietary filtered recipes
                                                            const params = {};
                                                            
                                                            if (activeCategory) {
                                                                params.categories = activeCategory;
                                                            } else if (activeDietary) {
                                                                if (activeDietary === 'vegetarian') {
                                                                    params.isVegetarian = 'true';
                                                                } else if (activeDietary === 'vegan') {
                                                                    params.isVegan = 'true';
                                                                }
                                                            }
                                                            
                                                            if (userID) {
                                                                params.userId = userID;
                                                                params.calculateCosts = 'true';
                                                            }
                                                            
                                                            const response = await recipeCatalogGet('/recipes/search', params);
                                                            const recipeList = response.data && Array.isArray(response.data) ? response.data : [];
                                                            await preFetchNutrition(recipeList);
                                                            setRecipes(recipeList);
                                                            setCostsCalculated(true);
                                                        } else {
                                                            // Refetch featured recipes with costs
                                                            const featuredParams = userID
                                                                ? { calculateCosts: 'true', userId: userID }
                                                                : { calculateCosts: 'true' };

                                                            const response = await recipeCatalogGet('/recipes/featured', featuredParams);
                                                            const recipeList = response.data && Array.isArray(response.data) ? response.data : [];
                                                            await preFetchNutrition(recipeList);
                                                            setFeaturedRecipes(recipeList);
                                                            setCostsCalculated(true);
                                                        }
                                                    } catch (error) {
                                                        console.error('Error calculating costs:', error);
                                                        alert('Error calculating recipe costs. Please try again.');
                                                    } finally {
                                                        setIsLoading(false);
                                                    }
                                                }}
                                                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors w-full"
                                                data-guide-target="calculate-costs-button"
                                            >
                                                Calculate All Costs
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-2 xl:grid-cols-4 xl:gap-1.5 2xl:grid-cols-5 2xl:gap-1">
                            {sortRecipes(filterRecipes(activeCategory || activeDietary ? recipes : featuredRecipes)).map((recipe, index) => (
                                    <div key={recipe._id} data-recipe-id={recipe._id} data-guide-target={index === 0 ? "first-recipe" : undefined}>
                                        <ModernRecipeCard recipe={recipe} />
                                    </div>
                            ))}
                        </div>
                    </div>
                )}
                </div>
            </div>

            {/* Compact Social Proof Section */}
            <div className="py-4 bg-gray-50">
                <div className="px-3 text-center max-w-7xl mx-auto">
                    <h2 className="font-semibold text-lg text-gray-900 mb-2">
                    Start Cooking Better Meals for Less.
                    </h2>
                    <p className="text-sm text-gray-600">
                    Joing me in this journey to make cooking easier and more affordable.
                    </p>
                </div>
            </div>

            {/* Search-only onboarding walkthrough */}
            <Walkthrough
                isOpen={showGuide}
                navigate={navigate}
                steps={[
                    {
                        target: '[data-guide-target="search-bar"]',
                        title: 'Welcome to GamePlan!',
                        content: 'Search for any recipe by name or ingredients. Type what you have and hit Search — we\'ll find the best matches for you.',
                        allowInteraction: false
                    },
                    {
                        target: '.category-nav-container',
                        title: 'Browse by Category',
                        content: 'Scroll through these tabs to browse recipes by category — Chicken, Breakfasts, Braai, Baked Goods and more.',
                        allowInteraction: false
                    },
                    {
                        target: '[data-guide-target="filter-button"]',
                        title: 'Filter & Sort',
                        content: 'Tap here to sort by cooking time, nutrition, or your cupboard ingredients. You can also filter by specials and more.',
                        allowInteraction: false
                    },
                    {
                        target: null,
                        title: 'Build Your Cupboard',
                        content: 'Now let\'s add your ingredients! Head to your cupboard and take a photo of your fridge or pantry — our AI will identify everything automatically, then find the best recipes you can make.',
                        allowInteraction: false,
                        navigateTo: '/cupboard',
                        walkthroughNextState: { active: true, page: 'cupboard' }
                    }
                ]}
                onComplete={async () => {
                    if (userID && tokenValidated) {
                        try {
                            await axios.put(`${API_BASE_URL}/users/walkthrough/search/complete`,
                                { userId: userID },
                                { headers: getAuthHeaders() }
                            );
                        } catch (error) {
                            console.error("Error marking walkthrough as completed:", error);
                        }
                    }
                    setHasSeenWalkthrough(true);
                    setShowGuide(false);
                }}
                onSkip={async () => {
                    if (userID && tokenValidated) {
                        try {
                            await axios.put(`${API_BASE_URL}/users/walkthrough/search/complete`,
                                { userId: userID },
                                { headers: getAuthHeaders() }
                            );
                        } catch (error) {
                            console.error("Error marking walkthrough as completed:", error);
                        }
                    }
                    setHasSeenWalkthrough(true);
                    setShowGuide(false);
                }}
                onClose={async () => {
                    if (userID && tokenValidated) {
                        try {
                            await axios.put(`${API_BASE_URL}/users/walkthrough/search/complete`,
                                { userId: userID },
                                { headers: getAuthHeaders() }
                            );
                        } catch (error) {
                            console.error("Error marking walkthrough as completed:", error);
                        }
                    }
                    setHasSeenWalkthrough(true);
                    setShowGuide(false);
                }}
            />

            {/* Sign-in Popup for Non-authenticated Users */}
            <SignInPopup 
                show={showSignInPopup} 
                onClose={handleCloseSignInPopup} 
            />

            {/* Save options popup: saved recipes + meal prep draft */}
            <SaveOptionsPopup
                show={saveOptionsPopup.show}
                onClose={() => setSaveOptionsPopup({ show: false, recipeId: null, recipeName: null })}
                recipeId={saveOptionsPopup.recipeId}
                recipeName={saveOptionsPopup.recipeName}
                isSaved={saveOptionsPopup.recipeId ? savedRecipes.some(id => id.toString() === saveOptionsPopup.recipeId.toString()) : false}
                isInMealPrepDraft={saveOptionsPopup.recipeId ? mealPrepDraftIds.includes(saveOptionsPopup.recipeId.toString()) : false}
                onAddToSaved={() => saveOptionsPopup.recipeId && saveRecipe(saveOptionsPopup.recipeId)}
                onRemoveFromSaved={() => saveOptionsPopup.recipeId && unsaveRecipe(saveOptionsPopup.recipeId)}
                onAddToMealPrepDraft={() => saveOptionsPopup.recipeId && addToMealPrepDraft(saveOptionsPopup.recipeId)}
                onRemoveFromMealPrepDraft={() => saveOptionsPopup.recipeId && removeFromMealPrepDraft(saveOptionsPopup.recipeId)}
            />

            {/* Frankie voice search — removed */}
            {/* <SearchFrankie onSearchAction={handleFrankieSearch} /> */}
        </div>
    );
}
